// src/components/auth/AuthContext.jsx
// Phase 1 auth rewrite (2026-04-23): client-side USERS dict deleted.
// All auth now talks to the backend /auth/* endpoints over HTTP-only cookies.
// See docs/phase-1-auth-design.md §8 for design rationale.

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();
const API_BASE = process.env.REACT_APP_API_URL || '';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Small fetch helper that always sends cookies. State-changing calls (POST/PUT/etc.)
// mirror the csrf_token cookie into the X-CSRF-Token header (double-submit pattern).
const _csrfHeader = () => {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? { 'X-CSRF-Token': decodeURIComponent(m[1]) } : {};
};

async function authFetch(path, { method = 'GET', body } = {}) {
  const needsCsrf = method !== 'GET' && method !== 'HEAD';
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(needsCsrf ? _csrfHeader() : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res;
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: ask the server who we are. If the access_token cookie is valid,
  // /auth/me returns the user profile. Any failure → we're logged out.
  const refreshUser = useCallback(async () => {
    try {
      const res = await authFetch('/auth/me');
      if (res.ok) {
        const profile = await res.json();
        setUser(profile);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (err) {
      console.error('Session check failed:', err);
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await refreshUser();
      setLoading(false);
    })();
  }, [refreshUser]);

  const login = async (username, password) => {
    try {
      const res = await authFetch('/auth/login', {
        method: 'POST',
        body: { username, password },
      });
      if (res.ok) {
        const body = await res.json();
        // MFA-enabled users get an mfa_token instead of session cookies.
        // Caller should redirect to the MFA step.
        if (body.mfa_required) {
          return {
            success: false,
            mfaRequired: true,
            mfaToken: body.mfa_token,
            expiresInSeconds: body.expires_in_seconds,
          };
        }
        // Non-MFA path — body is a UserProfile.
        setUser(body);
        setIsAuthenticated(true);
        return { success: true, user: body };
      }
      let detail = 'Invalid credentials';
      try {
        const body = await res.json();
        detail = body.detail || detail;
      } catch { /* non-JSON response */ }
      return { success: false, error: detail };
    } catch (err) {
      console.error('Login error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  // MFA login step 2 — exchange (challenge token + TOTP code OR recovery code)
  // for session cookies. Pass exactly one of `code` or `recoveryCode`.
  const verifyMfaLogin = async (mfaToken, { code, recoveryCode } = {}) => {
    try {
      const body = { mfa_token: mfaToken };
      if (code) body.code = code;
      if (recoveryCode) body.recovery_code = recoveryCode;
      const res = await authFetch('/auth/mfa/login-verify', {
        method: 'POST',
        body,
      });
      if (res.ok) {
        const profile = await res.json();
        setUser(profile);
        setIsAuthenticated(true);
        return { success: true, user: profile };
      }
      let detail = 'Invalid verification code';
      try {
        const body = await res.json();
        detail = body.detail || detail;
      } catch { /* non-JSON */ }
      return { success: false, error: detail };
    } catch (err) {
      console.error('MFA verify error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  // MFA enrolment — generate a fresh secret. Returns {secret, provisioningUrl}.
  // mfa_enabled stays false until verifyMfaSetup() confirms a working code.
  const setupMfa = async () => {
    try {
      const res = await authFetch('/auth/mfa/setup', { method: 'POST' });
      if (res.ok) {
        const body = await res.json();
        return { success: true, secret: body.secret, provisioningUrl: body.provisioning_url };
      }
      return { success: false, error: 'Could not start MFA setup' };
    } catch (err) {
      console.error('MFA setup error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  // MFA enrolment confirmation — flips mfa_enabled=true and returns the
  // freshly-generated recovery codes. These are visible exactly once;
  // the caller MUST surface them to the user before navigating away.
  const verifyMfaSetup = async (code) => {
    try {
      const res = await authFetch('/auth/mfa/verify-setup', {
        method: 'POST',
        body: { code },
      });
      if (res.ok) {
        const body = await res.json();
        await refreshUser();
        return { success: true, recoveryCodes: body.recovery_codes || [] };
      }
      let detail = 'Invalid verification code';
      try {
        const body = await res.json();
        detail = body.detail || detail;
      } catch { /* non-JSON */ }
      return { success: false, error: detail };
    } catch (err) {
      console.error('MFA verify-setup error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  // Rotate recovery codes. Old set is invalidated; new set is shown once.
  const regenerateRecoveryCodes = async (password, code) => {
    try {
      const res = await authFetch('/auth/mfa/regenerate-recovery-codes', {
        method: 'POST',
        body: { password, code },
      });
      if (res.ok) {
        const body = await res.json();
        return { success: true, recoveryCodes: body.recovery_codes || [] };
      }
      let detail = 'Could not regenerate recovery codes';
      try {
        const body = await res.json();
        detail = body.detail || detail;
      } catch { /* non-JSON */ }
      return { success: false, error: detail };
    } catch (err) {
      console.error('MFA regenerate-recovery-codes error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  // MFA disable — defence in depth: requires BOTH password and current TOTP code.
  const disableMfa = async (password, code) => {
    try {
      const res = await authFetch('/auth/mfa/disable', {
        method: 'POST',
        body: { password, code },
      });
      if (res.status === 204) {
        await refreshUser();
        return { success: true };
      }
      let detail = 'Could not disable MFA';
      try {
        const body = await res.json();
        detail = body.detail || detail;
      } catch { /* non-JSON */ }
      return { success: false, error: detail };
    } catch (err) {
      console.error('MFA disable error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const res = await authFetch('/auth/change-password', {
        method: 'POST',
        body: { current_password: currentPassword, new_password: newPassword },
      });
      if (res.status === 204) {
        return { success: true };
      }
      let detail = 'Password change failed';
      try {
        const body = await res.json();
        detail = body.detail || detail;
      } catch { /* non-JSON response */ }
      return { success: false, error: detail };
    } catch (err) {
      console.error('Change password error:', err);
      return { success: false, error: 'Network error — please try again' };
    }
  };

  const logout = async () => {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
    } catch (err) {
      // Even if the call fails, clear local state — the cookies may already be bad
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Back-compat for any code still calling hasPermission(). Post-rewrite,
  // callers should prefer explicit role checks (user.role === 'admin').
  const hasPermission = (permission) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return false;
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    verifyMfaLogin,
    setupMfa,
    verifyMfaSetup,
    disableMfa,
    regenerateRecoveryCodes,
    logout,
    changePassword,
    hasPermission,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
